/**
 * 门店数据加载队列：全屏最多 4 个酒店同时加载，每店串行 1 个组件（先出一个就展示，首屏更快）
 * 每个组件单独看进度，超时/失败则标记该组件并继续下一个
 */
import { getStoreRevenueDataByComponent } from '../../service/storeRevenueApi';
/** 12 个组件的请求顺序（与 StoreDataLoader 渲染顺序一致） */
export var COMPONENT_TYPES_ORDER = [
    'room-status',
    'channel-price',
    'room-status-recently',
    'order-traffic',
    'revpar',
    'room-health',
    'customer-source',
    'discount-analysis',
    'channel-analysis',
    'channel-info',
    'occupancy-analysis',
    'operation-efficiency',
];
/** 全屏同时最多 4 个酒店队列，减少「排队中」导致整屏一直转圈 */
var MAX_ACTIVE_QUEUES = 4;
/** 每店串行加载组件：先出一个组件就展示，首屏更快有内容；避免 3 个一起等导致长时间全 loading */
var COMPONENT_CONCURRENCY_PER_HOTEL = 1;
/** 单组件请求超时（毫秒），超时则标记该组件失败并继续下一个 */
var COMPONENT_REQUEST_TIMEOUT_MS = 45 * 1000;
var pending = [];
var active = new Map();
function withTimeout(p, ms, label) {
    return new Promise(function (resolve, reject) {
        var t = setTimeout(function () { return reject(new Error("".concat(label, " \u8BF7\u6C42\u8D85\u65F6(").concat(ms / 1000, "s)"))); }, ms);
        p.then(function (v) {
            clearTimeout(t);
            resolve(v);
        }, function (e) {
            clearTimeout(t);
            reject(e);
        });
    });
}
function runNext() {
    if (active.size >= MAX_ACTIVE_QUEUES || pending.length === 0)
        return;
    var item = pending.shift();
    if (!item || item.aborted) {
        if (pending.length > 0)
            runNext();
        return;
    }
    var k = String(item.storeId);
    active.set(k, {
        storeId: item.storeId,
        nextIndexToStart: 0,
        inFlight: new Set(),
        onSlice: item.onSlice,
        onComponentFail: item.onComponentFail,
        onComplete: item.onComplete,
        onError: item.onError,
        aborted: item.aborted,
    });
    tryStartMore(k);
}
function tryStartMore(k) {
    var state = active.get(k);
    if (!state || state.aborted) {
        active.delete(k);
        runNext();
        return;
    }
    while (state.inFlight.size < COMPONENT_CONCURRENCY_PER_HOTEL && state.nextIndexToStart < COMPONENT_TYPES_ORDER.length) {
        var index = state.nextIndexToStart;
        state.nextIndexToStart += 1;
        state.inFlight.add(index);
        startOne(k, index);
    }
    if (state.nextIndexToStart === COMPONENT_TYPES_ORDER.length && state.inFlight.size === 0) {
        active.delete(k);
        state.onComplete();
        runNext();
    }
}
function startOne(k, index) {
    var state = active.get(k);
    if (!state || state.aborted) {
        active.delete(k);
        runNext();
        return;
    }
    var storeId = state.storeId;
    var componentType = COMPONENT_TYPES_ORDER[index];
    withTimeout(getStoreRevenueDataByComponent(storeId, componentType), COMPONENT_REQUEST_TIMEOUT_MS, componentType)
        .then(function (data) {
        var current = active.get(k);
        if (!current || current.aborted) {
            active.delete(k);
            runNext();
            return;
        }
        current.inFlight.delete(index);
        current.onSlice(componentType, data);
        tryStartMore(k);
    })
        .catch(function (err) {
        var _a;
        var current = active.get(k);
        if (!current || current.aborted) {
            active.delete(k);
            runNext();
            return;
        }
        current.inFlight.delete(index);
        var reason = ((_a = err === null || err === void 0 ? void 0 : err.message) === null || _a === void 0 ? void 0 : _a.includes('超时')) ? 'timeout' : 'error';
        current.onComponentFail(componentType, reason);
        if (process.env.NODE_ENV === 'development') {
            console.warn("[StoreDataQueue] \u7EC4\u4EF6 ".concat(componentType, " hotelId=").concat(storeId, " ").concat(reason === 'timeout' ? '超时' : '失败', ":"), err === null || err === void 0 ? void 0 : err.message);
        }
        tryStartMore(k);
    });
}
/**
 * 将门店加入加载队列；全屏最多 4 个酒店同时跑，每店串行 1 个组件，单组件 45s 超时
 * @returns 取消函数，调用后该店未发请求不再发、已发出的忽略结果
 */
export function enqueueStoreData(storeId, callbacks) {
    var k = String(storeId);
    var item = {
        storeId: storeId,
        onSlice: callbacks.onSlice,
        onComponentFail: callbacks.onComponentFail,
        onComplete: callbacks.onComplete,
        onError: callbacks.onError,
        aborted: false,
    };
    pending.push(item);
    runNext();
    return function () {
        item.aborted = true;
        var a = active.get(k);
        if (a)
            a.aborted = true;
    };
}
